// ── Adapter Supabase do ISuperAdminRepository ─────────────────────
// Única camada que conhece as tabelas admin e a service-role key (createUser).

import { getSupabase, getSupabaseAdmin } from '@infra/supabase/client';
import type { Database } from '@/types/database.types';
import type {
  ISuperAdminRepository,
  AdminCreateUserParams,
  CreateWorkspaceParams,
  LogActivityParams,
  ProfileRow,
  WorkspaceRow,
  TimeRow,
  FunnelRow,
  PipelineStageRow,
  AdminActivityLogWithActor,
  AdminActivityFeedItem,
  WorkspaceMemberWithWorkspace,
  WorkspaceMemberWithProfile,
  WorkspaceMemberWithProfileByWorkspace,
  TimeMemberWithTime,
  TimeMemberWithProfile,
} from '@modules/superAdmin';

type PlatformRole = Database['public']['Tables']['profiles']['Row']['platform_role'];
type WsRole = Database['public']['Tables']['workspace_members']['Row']['role'];
type TeamRole = Database['public']['Tables']['time_members']['Row']['role'];

const client = () => {
  const s = getSupabase();
  if (!s) throw new Error('Supabase nao inicializado.');
  return s;
};

const adminClient = () => {
  const a = getSupabaseAdmin();
  if (!a) throw new Error('service_role nao configurado (REACT_APP_SUPABASE_SERVICE_ROLE_KEY).');
  return a;
};

const WM_WITH_PROFILE = 'id, workspace_id, user_id, role, ativo, profiles!workspace_members_user_id_fkey ( id, nome, initials, color, avatar_url, platform_role )';
const ALOG_WITH_ACTOR = 'id, created_at, actor_id, action, entity_type, entity_id, workspace_id, description, metadata, profiles!admin_activity_log_actor_id_fkey ( nome, initials, color, avatar_url )';

export class SupabaseSuperAdminRepository implements ISuperAdminRepository {
  // ── Profiles ────────────────────────────────────────────────────
  listAllProfiles = async (): Promise<ProfileRow[]> => {
    const { data } = await client()
      .from('profiles').select('id, nome, initials, platform_role, ativo, color, avatar_url').order('nome');
    return (data || []) as unknown as ProfileRow[];
  };

  updateProfileRole = async (profileId: string, role: string): Promise<void> => {
    const { error } = await client().from('profiles').update({ platform_role: role as PlatformRole }).eq('id', profileId);
    if (error) throw error;
    await this.logActivity({ action: 'user.platform_role_update', entityType: 'user', entityId: profileId, description: `Papel de plataforma alterado para "${role}"`, metadata: { role } });
  };

  setProfileActive = async (profileId: string, ativo: boolean): Promise<void> => {
    const { error } = await client().from('profiles').update({ ativo }).eq('id', profileId);
    if (error) throw error;
    await this.logActivity({ action: 'user.active_toggle', entityType: 'user', entityId: profileId, description: ativo ? 'Usuário ativado' : 'Usuário desativado', metadata: { ativo } });
  };

  adminCreateUser = async ({ email, password, nome, workspaceId, workspaceRole }: AdminCreateUserParams): Promise<void> => {
    const admin = adminClient();
    const { data, error } = await admin.auth.admin.createUser({ email: email.trim(), password, email_confirm: true });
    if (error) throw error;
    const userId = data?.user?.id;
    if (!userId) throw new Error('Auth criou o usuario mas nao retornou id.');
    const initials = nome.trim().split(/\s+/).map((p) => p[0]).join('').toUpperCase().slice(0, 2);
    const { error: profileErr } = await admin
      .from('profiles')
      .insert({ id: userId, nome: nome.trim(), initials, platform_role: 'member', ativo: true, first_access: true });
    if (profileErr) throw profileErr;
    const { error: wmErr } = await admin
      .from('workspace_members')
      .insert({ workspace_id: workspaceId, user_id: userId, role: workspaceRole as WsRole, ativo: true });
    if (wmErr) throw wmErr;
    await this.logActivity({ action: 'user.create', entityType: 'user', entityId: userId, workspaceId, description: `Usuário "${nome.trim()}" (${email.trim()}) criado`, metadata: { email: email.trim(), nome: nome.trim(), workspaceRole } });
  };

  // ── Auth admin: e-mail, senha, impersonação (service-role) ───────
  getAuthUserEmail = async (userId: string): Promise<string | null> => {
    const { data, error } = await adminClient().auth.admin.getUserById(userId);
    if (error) throw error;
    return data?.user?.email ?? null;
  };

  updateUserEmail = async (userId: string, newEmail: string): Promise<void> => {
    const email = newEmail.trim();
    const { error } = await adminClient().auth.admin.updateUserById(userId, { email, email_confirm: true });
    if (error) throw error;
    await this.logActivity({ action: 'user.email_update', entityType: 'user', entityId: userId, description: `E-mail alterado para "${email}"`, metadata: { email } });
  };

  setUserPassword = async (userId: string, password: string): Promise<void> => {
    const { error } = await adminClient().auth.admin.updateUserById(userId, { password });
    if (error) throw error;
    await this.logActivity({ action: 'user.password_reset', entityType: 'user', entityId: userId, description: 'Senha redefinida pelo super admin', metadata: {} });
  };

  signInAsUser = async (userId: string): Promise<void> => {
    const admin = adminClient();
    const { data: userData, error: getErr } = await admin.auth.admin.getUserById(userId);
    if (getErr) throw getErr;
    const email = userData?.user?.email;
    if (!email) throw new Error('Usuário não possui e-mail em auth.users.');
    // Registra ANTES de trocar a sessão: depois do verifyOtp o actor passa a ser o alvo.
    await this.logActivity({ action: 'user.impersonate', entityType: 'user', entityId: userId, description: `Super admin acessou a conta de "${email}"`, metadata: { email } });
    const { data: linkData, error: linkErr } = await admin.auth.admin.generateLink({ type: 'magiclink', email });
    if (linkErr) throw linkErr;
    const tokenHash = linkData?.properties?.hashed_token;
    if (!tokenHash) throw new Error('Não foi possível gerar o token de acesso.');
    const { error: otpErr } = await client().auth.verifyOtp({ token_hash: tokenHash, type: 'magiclink' });
    if (otpErr) throw otpErr;
  };

  // ── Workspaces ──────────────────────────────────────────────────
  listWorkspacesAdmin = async (): Promise<WorkspaceRow[]> => {
    const { data } = await client().from('workspaces').select('id, name, slug, ativo, created_at, settings').order('name');
    return (data || []) as unknown as WorkspaceRow[];
  };

  listActiveWorkspaces = async (): Promise<WorkspaceRow[]> => {
    const { data } = await client().from('workspaces').select('id, name, slug, ativo').eq('ativo', true).order('name');
    return (data || []) as unknown as WorkspaceRow[];
  };

  createWorkspace = async ({ name, slug, ownerUserId }: CreateWorkspaceParams): Promise<WorkspaceRow | null> => {
    const supabase = client();
    const { data: ws, error: wsErr } = await supabase
      .from('workspaces').insert({ name: name.trim(), slug, ativo: true }).select('id, name, slug, ativo').single();
    if (wsErr) throw wsErr;
    if (ownerUserId && ws?.id) {
      const { error: memErr } = await supabase
        .from('workspace_members').insert({ workspace_id: ws.id, user_id: ownerUserId, role: 'owner', ativo: true });
      if (memErr) throw memErr;
    }
    if (ws?.id) {
      await this.logActivity({ action: 'workspace.create', entityType: 'workspace', entityId: ws.id, workspaceId: ws.id, description: `Workspace "${name.trim()}" criado`, metadata: { name: name.trim(), slug } });
    }
    return (ws ?? null) as unknown as WorkspaceRow | null;
  };

  setWorkspaceActive = async (workspaceId: string, ativo: boolean): Promise<void> => {
    const { error } = await client().from('workspaces').update({ ativo }).eq('id', workspaceId);
    if (error) throw error;
    await this.logActivity({ action: 'workspace.active_toggle', entityType: 'workspace', entityId: workspaceId, workspaceId, description: ativo ? 'Workspace ativado' : 'Workspace desativado', metadata: { ativo } });
  };

  // Grava uma flag em `workspaces.settings` preservando as demais chaves
  // (`crm_mode`, `company_scope`…). Via RPC porque o merge do jsonb precisa
  // acontecer no banco: ler aqui e reescrever a coluna inteira perderia o que
  // outro painel tivesse gravado no meio do caminho. A própria função confere
  // o platform owner.
  setWorkspaceSettingsFlag = async (workspaceId: string, key: string, enabled: boolean): Promise<Record<string, unknown>> => {
    const { data, error } = await client().rpc('set_workspace_settings_flag', {
      p_workspace_id: workspaceId,
      p_key: key,
      p_enabled: enabled,
    });
    if (error) throw error;
    const settings = (data ?? {}) as Record<string, unknown>;

    await this.logActivity({
      action: 'workspace.settings_flag',
      entityType: 'workspace',
      entityId: workspaceId,
      workspaceId,
      description: `Flag "${key}" ${enabled ? 'ligada' : 'desligada'}`,
      metadata: { key, enabled },
    });
    return settings;
  };

  // ── Workspace members ───────────────────────────────────────────
  listWorkspaceMembersWithWorkspace = async (): Promise<WorkspaceMemberWithWorkspace[]> => {
    const { data } = await client()
      .from('workspace_members').select('user_id, role, ativo, workspaces ( id, name )').eq('ativo', true);
    return (data || []) as unknown as WorkspaceMemberWithWorkspace[];
  };

  listWorkspaceMembersWithProfile = async (): Promise<WorkspaceMemberWithProfile[]> => {
    const { data } = await client().from('workspace_members').select(WM_WITH_PROFILE).eq('ativo', true);
    return (data || []) as unknown as WorkspaceMemberWithProfile[];
  };

  listWorkspaceMembersByWorkspace = async (workspaceId: string): Promise<WorkspaceMemberWithProfileByWorkspace[]> => {
    const { data } = await client()
      .from('workspace_members')
      .select('user_id, role, atende_comercial, profiles!workspace_members_user_id_fkey ( id, nome, initials, color, avatar_url, platform_role, ativo )')
      .eq('workspace_id', workspaceId).eq('ativo', true);
    return (data || []) as unknown as WorkspaceMemberWithProfileByWorkspace[];
  };

  setWorkspaceMemberComercial = async (workspaceId: string, userId: string, value: boolean): Promise<void> => {
    const { error } = await client()
      .from('workspace_members')
      .update({ atende_comercial: value })
      .eq('workspace_id', workspaceId)
      .eq('user_id', userId);
    if (error) throw error;
    await this.logActivity({ action: 'workspace_member.comercial_toggle', entityType: 'workspace_member', entityId: userId, workspaceId, description: value ? 'Membro marcado como comercial (atende reuniões)' : 'Membro desmarcado do comercial', metadata: { value } });
  };

  upsertWorkspaceMember = async (workspaceId: string, userId: string, role: string): Promise<void> => {
    const { error } = await client()
      .from('workspace_members')
      .upsert({ workspace_id: workspaceId, user_id: userId, role: role as WsRole, ativo: true }, { onConflict: 'workspace_id,user_id' });
    if (error) throw error;
    await this.logActivity({ action: 'workspace_member.upsert', entityType: 'workspace_member', entityId: userId, workspaceId, description: `Membro vinculado ao workspace como "${role}"`, metadata: { userId, role } });
  };

  setWorkspaceMemberActive = async (memberId: string, ativo: boolean): Promise<void> => {
    const { error } = await client().from('workspace_members').update({ ativo }).eq('id', memberId);
    if (error) throw error;
    await this.logActivity({ action: 'workspace_member.active_toggle', entityType: 'workspace_member', entityId: memberId, description: ativo ? 'Membro do workspace ativado' : 'Membro do workspace desativado', metadata: { ativo } });
  };

  deleteWorkspaceMember = async (memberId: string): Promise<void> => {
    // .select() para detectar o 204-sem-erro que a RLS devolve quando nao ha
    // policy de DELETE (ou ela nega): sem isso a remocao falha em silencio.
    const { data, error } = await client().from('workspace_members').delete().eq('id', memberId).select('id');
    if (error) throw error;
    if (!data?.length) throw new Error('Remoção bloqueada pela RLS (nenhuma linha afetada).');
    await this.logActivity({ action: 'workspace_member.delete', entityType: 'workspace_member', entityId: memberId, description: 'Membro removido do workspace (exclusão definitiva)' });
  };

  updateWorkspaceMemberRole = async (memberId: string, role: string): Promise<void> => {
    const { error } = await client().from('workspace_members').update({ role: role as WsRole }).eq('id', memberId);
    if (error) throw error;
    await this.logActivity({ action: 'workspace_member.role_update', entityType: 'workspace_member', entityId: memberId, description: `Papel do membro alterado para "${role}"`, metadata: { role } });
  };

  // ── Times / membros ─────────────────────────────────────────────
  listTimeMembersWithTime = async (): Promise<TimeMemberWithTime[]> => {
    const { data } = await client()
      .from('time_members').select('user_id, role, ativo, times ( id, name, workspace_id )').eq('ativo', true);
    return (data || []) as unknown as TimeMemberWithTime[];
  };

  listTimes = async (workspaceId: string): Promise<TimeRow[]> => {
    const { data, error } = await client()
      .from('times').select('id, name, ativo').eq('workspace_id', workspaceId).order('name');
    if (error) throw error;
    return (data || []) as unknown as TimeRow[];
  };

  listTimeMembersByTimeIds = async (timeIds: string[]): Promise<TimeMemberWithProfile[]> => {
    if (!timeIds.length) return [];
    const { data } = await client()
      .from('time_members')
      .select('id, role, ativo, time_id, user_id, profiles!time_members_user_id_fkey ( id, nome, initials, color, avatar_url, ativo )')
      .in('time_id', timeIds).eq('ativo', true);
    return (data || []) as unknown as TimeMemberWithProfile[];
  };

  createTime = async (name: string, workspaceId: string): Promise<void> => {
    const { error } = await client().from('times').insert({ name: name.trim(), workspace_id: workspaceId, ativo: true });
    if (error) throw error;
    await this.logActivity({ action: 'time.create', entityType: 'time', workspaceId, description: `Time "${name.trim()}" criado`, metadata: { name: name.trim() } });
  };

  upsertTimeMember = async (timeId: string, userId: string, role: string): Promise<void> => {
    const { error } = await client()
      .from('time_members')
      .upsert({ time_id: timeId, user_id: userId, role: role as TeamRole, ativo: true }, { onConflict: 'time_id,user_id,role' });
    if (error) throw error;
    await this.logActivity({ action: 'time_member.upsert', entityType: 'time_member', entityId: userId, description: `Membro vinculado ao time como "${role}"`, metadata: { timeId, userId, role } });
  };

  setTimeMemberActive = async (memberId: string, ativo: boolean): Promise<void> => {
    const { error } = await client().from('time_members').update({ ativo }).eq('id', memberId);
    if (error) throw error;
    await this.logActivity({ action: 'time_member.active_toggle', entityType: 'time_member', entityId: memberId, description: ativo ? 'Membro do time ativado' : 'Membro do time desativado', metadata: { ativo } });
  };

  updateTimeMemberRole = async (memberId: string, role: string): Promise<void> => {
    const { error } = await client().from('time_members').update({ role: role as TeamRole }).eq('id', memberId);
    if (error) throw error;
    await this.logActivity({ action: 'time_member.role_update', entityType: 'time_member', entityId: memberId, description: `Papel no time alterado para "${role}"`, metadata: { role } });
  };

  updateTimeName = async (timeId: string, name: string): Promise<void> => {
    const { error } = await client().from('times').update({ name }).eq('id', timeId);
    if (error) throw error;
    await this.logActivity({ action: 'time.rename', entityType: 'time', entityId: timeId, description: `Time renomeado para "${name}"`, metadata: { name } });
  };

  // ── Funnels / stages (admin) ────────────────────────────────────
  listFunnelsByWorkspace = async (workspaceId: string): Promise<FunnelRow[]> => {
    const { data } = await client()
      .from('funnels').select('id, name, description, ativo').eq('workspace_id', workspaceId).order('name');
    return (data || []) as unknown as FunnelRow[];
  };

  listStagesByPipelineIds = async (pipelineIds: string[]): Promise<PipelineStageRow[]> => {
    if (!pipelineIds.length) return [];
    const { data } = await client()
      .from('pipeline_stages').select('id, name, color, order_index, pipeline_id')
      .in('pipeline_id', pipelineIds).eq('ativo', true).order('order_index');
    return (data || []) as unknown as PipelineStageRow[];
  };

  toggleFunnelActive = async (funnelId: string, ativo: boolean): Promise<void> => {
    const { error } = await client().from('funnels').update({ ativo }).eq('id', funnelId);
    if (error) throw error;
    await this.logActivity({ action: 'funnel.active_toggle', entityType: 'funnel', entityId: funnelId, description: ativo ? 'Funil ativado' : 'Funil desativado', metadata: { ativo } });
  };

  updateStageName = async (stageId: string, name: string): Promise<void> => {
    const { error } = await client().from('pipeline_stages').update({ name }).eq('id', stageId);
    if (error) throw error;
    await this.logActivity({ action: 'stage.rename', entityType: 'stage', entityId: stageId, description: `Etapa renomeada para "${name}"`, metadata: { name } });
  };

  // ── Log de atividades (auditoria administrativa) ──────────────────
  logActivity = async (params: LogActivityParams): Promise<void> => {
    // Best-effort: uma falha de auditoria nunca deve derrubar a ação principal.
    try {
      const s = getSupabase();
      if (!s) return;
      const { data: userData } = await s.auth.getUser();
      const { error } = await s.from('admin_activity_log').insert({
        actor_id: userData?.user?.id ?? null,
        action: params.action,
        entity_type: params.entityType ?? null,
        entity_id: params.entityId ?? null,
        workspace_id: params.workspaceId ?? null,
        description: params.description ?? null,
        metadata: (params.metadata ?? {}) as never,
      });
      if (error) console.warn('[AdminActivityLog] falha ao registrar:', error.message);
    } catch (e) {
      console.warn('[AdminActivityLog] falha ao registrar:', (e as Error).message);
    }
  };

  listActivityLog = async (limit = 200): Promise<AdminActivityLogWithActor[]> => {
    const { data, error } = await client()
      .from('admin_activity_log')
      .select(ALOG_WITH_ACTOR)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw error;
    return (data || []) as unknown as AdminActivityLogWithActor[];
  };

  listActivityFeed = async (limit = 300): Promise<AdminActivityFeedItem[]> => {
    // RPC SECURITY DEFINER: unifica admin_activity_log + history (global, só owner).
    const { data, error } = await client()
      .rpc('admin_activity_feed' as never, { p_limit: limit } as never);
    if (error) throw error;
    return (data || []) as unknown as AdminActivityFeedItem[];
  };
}
